import { Currency } from "@/Apollo/schema";

/** The display currencies a buyer can pick, in the order the picker lists them. */
export const DISPLAY_CURRENCIES = [
  Currency.Eur,
  Currency.Gbp,
  Currency.Usd,
] as const;

/**
 * The first guess for a visitor who never picked a currency (WHO-5143), as the apps'
 * `guessCurrencyForCountry`: the visitor's country (Vercel's `x-vercel-ip-country`, read by
 * `/api/display-currency-guess`), GB → GBP, US → USD, anything else EUR. Not the browser's
 * language: a Dutch visitor with en-US settings is still in the Netherlands.
 */
export const currencyForCountry = (
  country: string | null | undefined
): Currency => {
  switch (country?.toUpperCase()) {
    case "GB":
      return Currency.Gbp;
    case "US":
      return Currency.Usd;
    default:
      return Currency.Eur;
  }
};

export const toKnownCurrency = (value: unknown): Currency | null =>
  DISPLAY_CURRENCIES.includes(value as Currency) ? (value as Currency) : null;

export type LoginSyncAction =
  /** Take the account's currency over locally. */
  | { type: "adopt"; currency: Currency }
  /** Save the buyer's own pick on an account that has none. */
  | { type: "push"; currency: Currency }
  | { type: "none" };

/**
 * What a sign-in means for the display currency (WHO-5143), worked out once per signed-in
 * member: **the account's currency wins; a pick is saved on an account that has none.** A pick
 * made while signed in is saved there and then (`useSetDisplayCurrency`), so it never needs this.
 * The first guess is never saved: it isn't a pick.
 */
export const loginSyncAction = ({
  account,
  picked,
}: {
  account: Currency | null | undefined;
  picked: Currency | null;
}): LoginSyncAction => {
  if (account) {
    return account === picked
      ? { type: "none" }
      : { type: "adopt", currency: account };
  }

  return picked ? { type: "push", currency: picked } : { type: "none" };
};
